"use client";

import NextLink from "next/link";
import { forwardRef, useCallback, type ComponentPropsWithoutRef } from "react";
import { isCrossAppHref } from "@marketing-sites/utils/cross-app-href";
import { useLocalizeHref } from "@marketing-sites/l10n/providers/localize-href-context";

type CrossAppLinkProps = ComponentPropsWithoutRef<"a"> & { href: string };

/**
 * Resolves the href CrossAppLink will actually navigate to. Callers that report
 * their own analytics `destination` must use this rather than the raw href, or
 * they report a different URL from the one the user visits. Returns a function
 * so it can be applied per item inside a `.map`.
 */
export function useCrossAppHrefResolver(): (href: string) => string {
  const localizeHref = useLocalizeHref();

  return useCallback((href: string) => localizeHref(href), [localizeHref]);
}

/**
 * Renders a native anchor for same-origin paths served by a separate app
 * behind the CDN (the React signup app at /signup) so next/link can't
 * SPA-intercept them, and a normal next/link everywhere else. See
 * isCrossAppHref / the Button primitive for the same guard.
 */
export const CrossAppLink = forwardRef<HTMLAnchorElement, CrossAppLinkProps>(
  ({ href, children, ...props }, ref) => {
    const resolvedHref = useCrossAppHrefResolver()(href);

    if (isCrossAppHref(href)) {
      return (
        <a ref={ref} href={resolvedHref} {...props}>
          {children}
        </a>
      );
    }
    return (
      <NextLink ref={ref} href={resolvedHref} {...props}>
        {children}
      </NextLink>
    );
  },
);

CrossAppLink.displayName = "CrossAppLink";
